import { useAuth } from '@/contexts/AuthContext';
import { SummaryRow } from '@/pages/onboarding/SummaryRow';
import type { SummaryStepProps } from '@/pages/onboarding/onboarding.types';
import { DETAIL_STEP, PROFESSION_STEP, WORK_STEP } from '@/pages/onboarding/onboardingOptions';
import {
  consultationsSummary,
  detailSummary,
  modeSummary,
  professionSummary,
} from '@/pages/onboarding/onboardingSummary';
import { piiTrainingStatusQueryOptions } from '@/services/piiTraining/usePiiTrainingStatus';
import { isGatePending, statusAccountId } from '@/services/statusGate/useStatusGate';
import {
  Icon,
  Label,
  Notification,
  NotificationDescription,
  NotificationIcon,
  Stack,
  Text,
  Textarea,
} from '@careflux/primitives';
import { useQuery } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';

export const SummaryStep = ({ copy, control, values, onEditStep, isEditMode, onSignatureEdit }: SummaryStepProps) => {
  const { user } = useAuth();
  const { data: piiStatus } = useQuery({
    ...piiTrainingStatusQueryOptions(statusAccountId(user)),
    enabled: !isEditMode && !!user,
    staleTime: Number.POSITIVE_INFINITY, // cannot change mid-onboarding; the guard re-checks on its own mount
  });
  // Only promise the PII training when it will actually follow.
  const showPiiNext = !isEditMode && isGatePending(piiStatus);
  const rows = [
    { label: copy.summary.rows.profession, value: professionSummary(values, copy), step: PROFESSION_STEP },
    { label: copy.summary.rows.consultations, value: consultationsSummary(values, copy), step: WORK_STEP },
    { label: copy.summary.rows.mode, value: modeSummary(values, copy), step: WORK_STEP },
    { label: copy.summary.rows.detail, value: detailSummary(values, copy), step: DETAIL_STEP },
  ];

  return (
    <Stack as="section" gap="lg">
      <dl>
        {rows.map((row) => (
          <SummaryRow
            key={row.label}
            label={row.label}
            value={row.value}
            editLabel={copy.summary.edit}
            onEdit={() => onEditStep(row.step)}
          />
        ))}
      </dl>
      <Controller
        name="letterSignature"
        control={control}
        render={({ field }) => (
          <Stack gap="sm">
            <Label htmlFor="letter-signature">
              {copy.summary.signatureLabel}
              <Text size="xs" color="muted">
                {copy.summary.signatureHint}
              </Text>
            </Label>
            <Textarea
              id="letter-signature"
              value={field.value}
              onChange={(event) => {
                onSignatureEdit();
                field.onChange(event);
              }}
              onBlur={field.onBlur}
              placeholder={copy.summary.signaturePlaceholder}
              maxLength={2000}
              rows={3}
            />
          </Stack>
        )}
      />
      {showPiiNext && (
        <Notification role="note" variant="info">
          <NotificationIcon>
            <Icon name="ShieldCheck" />
          </NotificationIcon>
          <NotificationDescription>{copy.summary.piiNext}</NotificationDescription>
        </Notification>
      )}
    </Stack>
  );
};
